import * as React from 'react';

/** How far, after the resistance, a pull must go to refresh. */
export const PULL_THRESHOLD = 64;
/** The farthest the indicator follows a finger. */
const PULL_MAX = 96;
/** Where the indicator rests while the refresh runs. */
const PULL_HOLD = 48;
/** Movement before a touch is taken as a pull, so a tap is never one. */
const SLOP = 8;
/** At least this long on screen, or a fast refresh reads as a flicker. */
const SHORTEST = 400;

export interface PullState {
  /** How far down the indicator is drawn, in px. */
  distance: number;
  /** A finger is on the glass, so the indicator follows without easing. */
  dragging: boolean;
  refreshing: boolean;
}

/**
 * Pull down from the top of a phone screen to refresh it, as a phone's
 * browser does: the finger draws an indicator down, with some resistance,
 * and letting go past the threshold runs `onRefresh` while it rests there.
 * Only a pull from the very top counts, only downward and more down than
 * across, so a table scrolled sideways or a list scrolled back up is left
 * alone. Without `onRefresh` it does nothing.
 *
 * While a screen can refresh, the page's own overscroll is contained, so a
 * phone's browser does not also reload the page under it.
 */
export function usePullToRefresh(
  ref: React.RefObject<HTMLElement | null>,
  onRefresh?: () => Promise<unknown> | unknown
): PullState {
  const [state, setState] = React.useState<PullState>({
    distance: 0,
    dragging: false,
    refreshing: false,
  });
  const latest = React.useRef(onRefresh);
  latest.current = onRefresh;
  const enabled = !!onRefresh;

  React.useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;
    // The element's own page: a gallery phone frame is an iframe, run from
    // the page around it.
    const view = element.ownerDocument.defaultView ?? window;
    const root = element.ownerDocument.documentElement;
    const before = root.style.overscrollBehaviorY;
    root.style.overscrollBehaviorY = 'contain';

    let startX = 0;
    let startY: number | null = null;
    let engaged = false;
    let distance = 0;
    let refreshing = false;

    const start = (event: TouchEvent) => {
      startY =
        refreshing || view.scrollY > 0 || event.touches.length !== 1
          ? null
          : event.touches[0].clientY;
      startX = event.touches[0]?.clientX ?? 0;
      engaged = false;
      distance = 0;
    };

    const move = (event: TouchEvent) => {
      if (startY === null) return;
      const dy = event.touches[0].clientY - startY;
      const dx = event.touches[0].clientX - startX;
      if (!engaged) {
        if (dy < SLOP && Math.abs(dx) < SLOP) return;
        // Up, or more across than down: a scroll, not a pull.
        if (dy < SLOP || Math.abs(dx) > dy) {
          startY = null;
          return;
        }
        engaged = true;
      }
      // Stops the rubber band and the page scrolling under the pull.
      event.preventDefault();
      distance = Math.min(PULL_MAX, (dy - SLOP) * 0.5);
      setState({ distance, dragging: true, refreshing: false });
    };

    const end = () => {
      if (startY === null || !engaged) {
        startY = null;
        return;
      }
      startY = null;
      if (distance < PULL_THRESHOLD) {
        setState({ distance: 0, dragging: false, refreshing: false });
        return;
      }
      refreshing = true;
      setState({ distance: PULL_HOLD, dragging: false, refreshing: true });
      const shortest = new Promise((resolve) => setTimeout(resolve, SHORTEST));
      Promise.all([Promise.resolve(latest.current?.()), shortest])
        .catch(() => undefined)
        .finally(() => {
          refreshing = false;
          setState({ distance: 0, dragging: false, refreshing: false });
        });
    };

    element.addEventListener('touchstart', start, { passive: true });
    element.addEventListener('touchmove', move, { passive: false });
    element.addEventListener('touchend', end);
    element.addEventListener('touchcancel', end);
    return () => {
      root.style.overscrollBehaviorY = before;
      element.removeEventListener('touchstart', start);
      element.removeEventListener('touchmove', move);
      element.removeEventListener('touchend', end);
      element.removeEventListener('touchcancel', end);
    };
  }, [ref, enabled]);

  return state;
}
